import { reportEvent } from '@/app/utils';
import { getFileExtension, getPreviewType, isFolder } from '../_components/File/utils';
import type { ClawFileItem } from '../types';
import { normalizePath } from './index';

type ClawCreationOperation = 'download' | 'delete' | 'share' | 'addChat';

export const reportClawCreationOperation = (operation: ClawCreationOperation, path?: string): void => {
  const creationPath = normalizePath(path);
  if (!creationPath) return;
  reportEvent('claw_creation_operation', {
    operation_type: operation,
    creation_path: creationPath,
  });
};

export const DEFAULT_MEDIA_ROOT = 'media';
const DOCUMENT_EXTENSIONS = new Set([
  'md',
  'markdown',
  'pdf',
  'csv',
  'txt',
  'json',
  'yaml',
  'yml',
  'xml',
  'ppt',
  'pptx',
]);
const ARCHIVE_EXTENSIONS = new Set(['zip', 'rar', '7z', 'tar', 'gz', 'tgz', 'bz2', 'xz']);
export const SKIPPED_FOLDERS = new Set(['skills', 'sessions', 'state', 'tmp', 'memory', 'corn', 'cron']);

export const CLAW_UPLOAD_ACCEPT =
  'image/*,video/*,audio/*,.md,.markdown,.pdf,.csv,.txt,.json,.yaml,.yml,.xml,.ppt,.pptx,.zip,.rar,.7z,.tar,.gz,.tgz,.bz2,.xz';

/** upload 槽限定类型 → 原生 <input accept>。空数组回退到全量白名单（不额外限制）。 */
const ACCEPT_KIND_TO_INPUT_ACCEPT: Record<'image' | 'video' | 'audio' | 'file', string> = {
  image: 'image/*',
  video: 'video/*',
  audio: 'audio/*',
  file: '.md,.markdown,.pdf,.csv,.txt,.json,.yaml,.yml,.xml,.ppt,.pptx,.zip,.rar,.7z,.tar,.gz,.tgz,.bz2,.xz',
};

export const buildUploadAcceptAttr = (accept: Array<'image' | 'video' | 'audio' | 'file'> | undefined): string => {
  if (!accept?.length) return CLAW_UPLOAD_ACCEPT;
  return accept.map((kind) => ACCEPT_KIND_TO_INPUT_ACCEPT[kind]).join(',');
};

/** 把一个 File 归类成 upload accept 的四档之一，用于发送前真校验（原生 accept 仅提示，可绕过）。 */
const classifyUploadFileKind = (file: File): 'image' | 'video' | 'audio' | 'file' => {
  if (file.type.startsWith('image/')) return 'image';
  if (file.type.startsWith('video/')) return 'video';
  if (file.type.startsWith('audio/')) return 'audio';
  return 'file';
};

export const isFileMatchingAccept = (
  file: File,
  accept: Array<'image' | 'video' | 'audio' | 'file'> | undefined,
): boolean => {
  if (!accept?.length) return true;
  return accept.includes(classifyUploadFileKind(file));
};

export const getPathSegments = (path: string) => normalizePath(path).split('/').filter(Boolean);

const isSystemFolderPath = (path: string) => {
  const segments = getPathSegments(path);
  const folderName = segments[segments.length - 1]?.toLowerCase();
  return !folderName || SKIPPED_FOLDERS.has(folderName) || folderName.startsWith('.');
};

const isSystemLikePath = (path: string) => getPathSegments(path).some((segment) => segment.startsWith('.'));

/** 把后端下发的任意 media_type 归一到四档之一，非 image/video/audio 一律落 file。 */
export const normalizeClawMediaType = (raw?: string): 'image' | 'video' | 'audio' | 'file' =>
  raw === 'image' || raw === 'video' || raw === 'audio' ? raw : 'file';

export const getMediaItemType = (item: ClawFileItem): 'image' | 'video' | 'audio' | 'file' =>
  normalizeClawMediaType(getPreviewType(item));

export const getMediaItemLabel = (item: ClawFileItem): 'image' | 'video' | 'audio' | 'document' | 'file' => {
  const mediaType = getMediaItemType(item);
  if (mediaType === 'image' || mediaType === 'video' || mediaType === 'audio') {
    return mediaType;
  }

  const extension = getFileExtension(item.name || item.path || '');
  if (DOCUMENT_EXTENSIONS.has(extension)) {
    return 'document';
  }

  // 压缩包等无法预览的二进制文件归入「文件」档，与 document 区分开。
  return 'file';
};

export const shouldDisplayMediaFile = (item: ClawFileItem) => {
  const path = normalizePath(item.path);
  if (!path || isFolder(item) || isSystemLikePath(path)) return false;

  const previewType = getPreviewType(item);
  if (previewType === 'image' || previewType === 'video' || previewType === 'audio') return true;

  const extension = getFileExtension(item.name || item.path || '');
  return DOCUMENT_EXTENSIONS.has(extension) || ARCHIVE_EXTENSIONS.has(extension);
};

export const isAllowedUploadFile = (file: File) => {
  const extension = getFileExtension(file.name || '');
  if (DOCUMENT_EXTENSIONS.has(extension) || ARCHIVE_EXTENSIONS.has(extension)) {
    return true;
  }

  if (file.type.startsWith('image/') || file.type.startsWith('video/') || file.type.startsWith('audio/')) {
    return true;
  }

  return false;
};

export const shouldDisplayFolder = (item: ClawFileItem) => {
  if (!isFolder(item)) return false;
  const path = normalizePath(item.path);
  if (!path) return false;
  return !isSystemFolderPath(path);
};

export const areItemsEqual = (prevItems: ClawFileItem[], nextItems: ClawFileItem[]) => {
  if (prevItems.length !== nextItems.length) return false;
  return prevItems.every((item, index) => {
    const nextItem = nextItems[index];
    return (
      normalizePath(item.path) === normalizePath(nextItem?.path) &&
      item.type === nextItem?.type &&
      item.last_modified === nextItem?.last_modified
    );
  });
};

export const formatLastModified = (value?: string) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};
